import React, { useState, useEffect } from "react";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import idwalaIcon from "@/styles/all-images/icons/idwala.svg";
import tegmulIcon from "@/styles/all-images/icons/tegmul.svg";
import fuelcapIcon from "@/styles/all-images/icons/fuelcap.svg";
import greencheck from "@/styles/all-images/icons/greencheck.svg";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";

interface FilterModalProps {
  onClose: () => void;
  onApply: (selectedCompanyId: number) => void;
  isOpen: boolean;
  currentFilter?: number;
}

interface Company {
  id: number;
  name: string;
  icon?: string;
}

const FilterModal = ({
  onClose,
  onApply,
  isOpen,
  currentFilter,
}: FilterModalProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    currentFilter || null,
  );
  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Default company icons mapping
  const companyIcons = {
    "Idwala Energy Pty Ltd.": idwalaIcon,
    "Tegmul Fabrication Pty Ltd.": tegmulIcon,
    "FuelCap Pty Ltd.": fuelcapIcon,
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(currentFilter || null);
      fetchCompanies();
    }
  }, [isOpen, currentFilter]);

  useEffect(() => {
    if (companiesList.length > 0 && isOpen) {
      setShowModal(true);
    }
  }, [companiesList, isOpen]);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("companyList");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
        }),
      );

      if (response.ok && response.data) {
        const companiesWithIcons = response.data.map((company: Company) => ({
          ...company,
          icon:
            companyIcons[company.name as keyof typeof companyIcons] ||
            idwalaIcon,
        }));
        setCompaniesList(companiesWithIcons);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    if (selectedCompanyId) {
      onApply(selectedCompanyId);
    }
    handleClose();
  };

  const handleClear = () => {
    setSelectedCompanyId(null);
    onApply(0); // Passing 0 to indicate no filter
    handleClose();
  };

  const handleClose = () => {
    setShowModal(false);
    onClose();
  };

  if (!showModal) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-end tw-z-50">
      <div className="tw-bg-white tw-shadow-lg tw-h-screen tw-w-[350px] tw-flex tw-flex-col">
        {/* Sticky Header */}
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <h2 className="tw-text-base tw-font-semibold">Filter</h2>
          <button onClick={handleClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4">
          <div>
            <label className="tw-text-sm tw-font-semibold tw-text-[#414651]">
              Select Company
            </label>
          </div>
          <div className="tw-space-y-2">
            {companiesList.map((company) => (
              <div
                key={company.id}
                className={`tw-flex tw-items-center tw-p-3 tw-rounded-lg tw-cursor-pointer tw-w-full tw-my-4 tw-border tw-border-[#E9EAEB] ${
                  selectedCompanyId === company.id
                    ? "tw-bg-[#ECFDF3] tw-border tw-border-[#E9EAEB] tw-text-[#027A48]"
                    : "tw-bg-white hover:tw-bg-gray-100 tw-text-[#027A48]"
                }`}
                onClick={() => setSelectedCompanyId(company.id)}
              >
                <img
                  src={company.icon}
                  className="tw-mr-3"
                  alt={`${company.name} logo`}
                />
                <span className="tw-flex-1 tw-ml-2 tw-text-[#232323]">
                  {company.name}
                </span>
                {selectedCompanyId === company.id && (
                  <img src={greencheck} alt="Green Check" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="tw-p-4 tw-flex tw-items-center tw-gap-4 tw-border-t tw-sticky tw-bottom-0 tw-bg-white tw-z-10">
          <button
            onClick={handleClear}
            className="tw-px-6 tw-py-2 tw-w-full tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-text-center"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="tw-text-white tw-w-full tw-rounded-md tw-py-2 tw-px-6 tw-bg-[#146C94]"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
